import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { Button, Card, DialogFooter } from '../../../components/ui';
import { Notice, RecordAvatar, SignalList, VerdictMark } from './ui';
import type { DuplicateEntityType, MatchHit } from '../../../types/duplicates';

interface MatchBeforeCreateProps {
  hits: MatchHit[];
  entityType: DuplicateEntityType;
  selectedId: string;
  onSelect: (id: string) => void;
  /** Available only when the caller knows how to fold the card into a record */
  canAttach: boolean;
  attaching: boolean;
  error?: string | null;
  onOpenExisting: (hit: MatchHit) => void;
  onAttach: (hit: MatchHit) => void;
  onCreateAnyway: () => void;
}

/**
 * The step between reading a card and filling the form: this person may
 * already be in the directory. The safe way out is the prominent one — a
 * second record is never created just because someone kept pressing on.
 */
export const MatchBeforeCreate: React.FC<MatchBeforeCreateProps> = ({
  hits,
  entityType,
  selectedId,
  onSelect,
  canAttach,
  attaching,
  error,
  onOpenExisting,
  onAttach,
  onCreateAnyway,
}) => {
  const selected = hits.find((hit) => hit.id === selectedId) || hits[0];
  const certain = hits.some((hit) => hit.verdict === 'certain');
  const single = hits.length === 1;

  if (!selected) return null;

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-5 pt-3 sm:px-5">
        <p className="text-[13px] text-ink">
          {single ? (
            <>
              This looks like <span className="font-medium">{selected.label}</span>,{' '}
              {certain ? 'already in the directory' : 'who may already be in the directory'}.
            </>
          ) : (
            <>{hits.length} records already look like this card.</>
          )}
        </p>

        <Card divided className="mt-3">
          {hits.map((hit) => {
            const isSelected = hit.id === selected.id;
            return (
              <div key={hit.id} className="p-3.5">
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={hit.label}
                  onClick={() => onSelect(hit.id)}
                  disabled={single}
                  className={`flex w-full items-center gap-2.5 text-left ${single ? 'cursor-default' : ''}`}
                >
                  <RecordAvatar name={hit.label} imageId={hit.imageId} kind={entityType} />
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-[13px] text-ink">{hit.label}</span>
                      {!single && isSelected && <Check className="h-3.5 w-3.5 flex-shrink-0 text-primary-600" strokeWidth={2} />}
                    </span>
                    <span className="block truncate text-[12px] text-neutral-500">{hit.subtitle || 'No role recorded'}</span>
                  </span>
                  <VerdictMark verdict={hit.verdict} score={hit.score} />
                </button>
                {hit.signals.length > 0 && isSelected && <SignalList signals={hit.signals} className="mt-2.5" />}
              </div>
            );
          })}
        </Card>

        {canAttach && (
          <p className="mt-2.5 text-[12px] text-neutral-400">
            Adding the card keeps {selected.label}, attaches the photo and fills only what is missing.
          </p>
        )}

        {error && (
          <Notice tone="error" className="mt-3">
            {error}
          </Notice>
        )}
      </div>

      <DialogFooter className="flex-wrap">
        <Button variant="ghost" size="sm" onClick={onCreateAnyway} disabled={attaching} className="mr-auto h-10 sm:h-9">
          Create anyway
        </Button>
        <Button variant="secondary" onClick={() => onOpenExisting(selected)} disabled={attaching} className="h-10 sm:h-9">
          Open
          <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
        </Button>
        {canAttach && (
          <Button
            variant="primary"
            onClick={() => onAttach(selected)}
            loading={attaching}
            disabled={attaching}
            className="h-10 sm:h-9"
          >
            Add card
          </Button>
        )}
      </DialogFooter>
    </>
  );
};
